const post = {
  slug: 'youtube-script-writing',
  cluster: 'scripting',
  pillar: true,
  eyebrow: 'Scripting',
  title: 'How to write a YouTube script people actually finish',
  description:
    'Most scripts fail the same way: they are correct, organised, and about their subject rather than inside it. Here is what to do instead.',
  keywords: [
    'youtube script writing', 'how to write a youtube script', 'youtube video script',
    'script structure', 'retention', 'faceless youtube script', 'narration script',
  ],
  published: '2026-08-21',
  hero: {
    src: '/blog/youtube-script-writing.webp',
    alt: 'A script page marked up with a highlighted opening question and an arrow tracing where it gets answered.',
  },
  lede:
    'A script can be accurate, well organised, and completely dead. That is the most common failure in this format, and it is not fixed by writing better sentences — it is fixed by deciding what the video is **doing** before you write any.',
  sections: [
    {
      heading: 'The failure almost every script shares',
      body: [
        {
          type: 'para',
          text: 'Write about a subject you know and you will produce something correct. It will have an introduction, four or five well-ordered points, and a conclusion that restates them. Nobody will finish it.',
        },
        {
          type: 'para',
          text: 'The problem is that it is *about* its subject rather than *inside* it. Nothing is at stake, nothing is pending, and the viewer has no reason to still be there in ninety seconds. Retention graphs make this look like a pacing problem, so people speed up their delivery and cut the pauses. That is treating a symptom.',
        },
        {
          type: 'para',
          text: 'The fix is structural, and it starts before the first sentence.',
        },
      ],
    },
    {
      heading: 'Decide the spine before you write',
      body: [
        {
          type: 'para',
          text: 'A subject is not a spine. "Bananas are interesting" is a subject. "The banana you eat is a clone losing a war against a fungus" is a spine — it is one sentence saying what the video argues, and everything either serves it or gets cut.',
        },
        {
          type: 'para',
          text: 'Write that sentence down before anything else. If you cannot, you do not yet have a video; you have a topic you were planning to talk around for eight minutes.',
        },
        {
          type: 'example',
          badLabel: 'A subject',
          goodLabel: 'A spine',
          bad: 'A video about the history of shipping containers.',
          good: 'One man with no shipping experience made ocean freight so cheap it moved the world\'s factories — and the industry fought him for twenty years.',
          note: 'The second one tells you what changes between the first line and the last. That is the test.',
        },
        {
          type: 'para',
          text: 'Then decide the **form** deliberately rather than defaulting to explainer. A hypothetical played straight, a second-person walkthrough, a countdown, a story told from inside the subject — all of these are legitimate, and a half-committed one is worse than not attempting it. Pick, then commit completely.',
        },
      ],
    },
    {
      heading: 'Open a loop and make the viewer wait',
      body: [
        {
          type: 'para',
          text: 'Tension is not hype. It is an unanswered question the viewer wants answered, held open on purpose.',
        },
        {
          type: 'list',
          ordered: true,
          items: [
            'Ask it in the first two sentences, and do not answer it yet.',
            'Refer back to it as you go, so the viewer knows you have not forgotten.',
            'Answer it exactly where you planned, and not before.',
            'When one loop closes, open the next in the same breath.',
          ],
        },
        {
          type: 'para',
          text: 'The most common version of this going wrong is resolving the hook in the first thirty seconds and then narrating an epilogue nobody asked for. Write down the question and the timestamp where it gets answered. The gap between those two points is the only thing keeping anyone watching, so make it deliberate rather than accidental.',
        },
        {
          type: 'callout',
          title: 'Order is a tool',
          text: 'State that something went wrong before you say what. Name a consequence before its cause. Mention a number and explain it a beat later. The same sentences in a different sequence are the difference between suspense and a summary.',
        },
      ],
    },
    {
      heading: 'Put the viewer in a place, not in a summary',
      body: [
        {
          type: 'para',
          text: 'Abstractions do not land. Specific moments do. Wherever the material allows, drop the viewer into a scene with a time, a place and a person doing something — then pull back out to the general point.',
        },
        {
          type: 'example',
          bad: 'In the nineteen fifties, shipping was slow and expensive.',
          good: 'It is nineteen fifty-six, and a truck driver in Newark is watching dockworkers move his cargo by hand, one crate at a time, for the ninth hour.',
          note: 'Same fact. The second one has a camera in the room — and it gives the edit something to actually show.',
        },
        {
          type: 'para',
          text: 'You do not need many. Two or three real moments carry a whole script: one at the open, one at the turn, and one anywhere the middle starts drifting into a list.',
        },
        {
          type: 'para',
          text: 'This is also where most scripts quietly start inventing. Use concrete physical detail — what is visible, what it costs, how long it takes — but only detail the material actually supports. You are choosing which true things to show, never adding new ones.',
        },
      ],
    },
    {
      heading: 'Give it someone to happen to',
      body: [
        {
          type: 'para',
          text: 'Facts attached to a person are remembered; facts floating free are not. Find the human being at the centre of the material — not "engineers" but the engineer, not "consumers" but the family — and name what it cost them.',
        },
        {
          type: 'para',
          text: 'Where a topic genuinely has no person in it, the stakes are still real: what fails, what it would take to fix, who lives with the consequences. Say that plainly rather than leaving the material weightless.',
        },
      ],
    },
    {
      heading: 'Be specific, because vagueness costs you twice',
      body: [
        {
          type: 'para',
          text: 'Prefer the name over the category, the year over "recently", the number over "a lot", the actual term over a paraphrase of it. This is worth doing for the viewer, but there is a second reason that matters more for a faceless channel.',
        },
        {
          type: 'para',
          text: '**Vague writing degrades your pictures.** "Malcolm McLean" and "1956" return real photographs. "An entrepreneur" and "the post-war years" return generic stock footage of nothing in particular. Whatever you are using to source visuals — a stock library, an image search, an archive — can only find what your script actually names. A script of correct but generic statements produces a video that looks like every other video on the subject, because it literally is showing the same footage.',
        },
        {
          type: 'para',
          text: 'There is more on this in [why a more specific script gives you better visuals](/blogs/specific-script-better-visuals).',
        },
        {
          type: 'callout',
          title: 'Specific and true, not specific-sounding',
          text: 'This never licenses inventing a name or sharpening "around forty percent" to "exactly forty-one percent" because it sounds better. Specific-and-false is the worst outcome available to you. See [handling facts you are not sure about](/blogs/handling-uncertain-facts-in-scripts).',
        },
      ],
    },
    {
      heading: 'Write for the ear, not the page',
      body: [
        {
          type: 'para',
          text: 'This gets skipped constantly and it is audible immediately. A narrator reads what you typed. Write "nineteen fifty-four", not "1954". Write "about thirty percent", not "~30%". Anything a reader silently converts, a narrator either mispronounces or reads literally.',
        },
        {
          type: 'para',
          text: 'Read the draft out loud. Every sentence you stumble on is one the narration will stumble on too. [Writing a script for narration](/blogs/writing-a-script-for-narration) covers the rest of this.',
        },
      ],
    },
    {
      heading: 'Sentence length is shot length',
      body: [
        {
          type: 'para',
          text: 'If your scenes cut at sentence boundaries — and most editing approaches land close to that — then a run of long sentences renders as a run of slow scenes. This is why a well-written script can still feel monotonous on screen: the prose is fine, the rhythm is flat.',
        },
        {
          type: 'para',
          text: 'Vary it deliberately. Short sentences and fragments at reveals, turns, and the beat right after a number — those become hard cuts. Longer sentences carrying mechanism and explanation become held shots. Never put two long sentences next to each other, and break any run of three at similar length.',
        },
        {
          type: 'para',
          text: 'More on this in [pacing is an edit, not prose](/blogs/script-pacing-and-shot-length).',
        },
      ],
    },
    {
      heading: 'The self-audit that catches the rest',
      body: [
        {
          type: 'para',
          text: 'Before you record, ask one question of the draft: **could this script have been written about a slightly different topic with a few words swapped?**',
        },
        {
          type: 'para',
          text: 'If yes, it is generic, and being correct will not save it. Go back and find the two specifics a generic treatment would not contain — a named person, a dated event, an exact figure, the term practitioners actually use. If you cannot find two, you have not researched the topic deeply enough to make the video yet.',
        },
        {
          type: 'para',
          text: 'The second question: what *changes* between the first line and the last? If nothing changes — if the script merely covers ground, point after point — it is an article read aloud.',
        },
      ],
    },
  ],
  faq: [
    {
      q: 'How long should a YouTube script be?',
      a: 'Roughly 150 words a minute of finished video is a reliable starting estimate for narration, though delivery varies. Write to the length the idea supports rather than padding to hit a target — see how long a script should be for the full answer.',
    },
    {
      q: 'Should I write a full script or use bullet points?',
      a: 'For a faceless channel, write the full script. Bullets work when a presenter can improvise on camera and carry it with delivery; narration has no delivery to fall back on, and the gaps show as filler.',
    },
    {
      q: 'Is it fine to use AI to write the script?',
      a: 'It is fine to use it as a drafting tool, but the failure mode is specific: models default to correct, evenly-weighted, generic prose — exactly the flat script described at the top of this page. If you use one, you still have to supply the spine, the moment, and the specifics. Those are the parts that make it a video.',
    },
  ],
  related: ['youtube-video-script-structure', 'youtube-retention-open-loops', 'specific-script-better-visuals'],
};

export default post;
